import React, { useState, useEffect } from 'react';
import { getCurrentUser } from '../utils/api';
import { 
  IoPersonOutline, 
  IoMailOutline, 
  IoSaveOutline,
  IoEyeOutline,
  IoEyeOffOutline,
  IoCheckmarkCircleOutline,
  IoAlertCircleOutline
} from 'react-icons/io5';
import './Profile.css';
import { API_URL } from '../utils/networkConfig';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load user data on component mount
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      setForm({
        name: currentUser.name || '',
        email: currentUser.email || '',
        password: '',
        confirmPassword: ''
      });
    }
  }, []);

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError('');
    if (success) setSuccess('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setForm({
      name: user.name || '',
      email: user.email || '',
      password: '',
      confirmPassword: ''
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    // Validate passwords match if password is being changed
    if (form.password && form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in to update your profile.');
        return;
      }

      // Prepare data - only include password if it's provided
      const updateData = {
        name: form.name.trim(),
        email: form.email.trim()
      };
      
      if (form.password.trim()) {
        updateData.password = form.password;
      }

      const res = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Update localStorage with new user data
        localStorage.setItem('userName', data.user.name);
        localStorage.setItem('userEmail', data.user.email);
        
        // Update local state
        setUser(data.user);
        setForm({
          name: data.user.name,
          email: data.user.email,
          password: ''
        });
        
        setSuccess('Profile updated successfully!');
        
        // Dispatch custom event to update header
        window.dispatchEvent(new CustomEvent('authChange'));
      } else {
        setError(data.error || 'Failed to update profile.');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleName = (roleId) => {
    switch(roleId) {
      case 1: return 'Admin';
      case 2: return 'Home Viewer';
      case 3: return 'Game Show Player';
      default: return 'User';
    }
  };

  const getRoleClass = (roleId) => {
    switch(roleId) {
      case 1: return 'admin';
      case 2: return 'home-viewer';
      case 3: return 'game-player';
      default: return 'game-player';
    }
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h2>My Profile</h2>
        <p>Manage your account information and settings</p>
      </div>

      <div className="profile-content">
        <div className="profile-card">
          {/* Profile Info Section */}
          <div className="profile-info">
            <div className="avatar-section">
              <div className="avatar">
                <IoPersonOutline className="avatar-icon" />
              </div>
              <div className="user-meta">
                <h3>{user.name}</h3>
                <span className={`role-badge ${getRoleClass(user.role_id)}`}>
                  {getRoleName(user.role_id)}
                </span>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-group">
              <label>
                <IoPersonOutline className="form-icon" />
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                disabled={!isEditing || isSubmitting}
                required
              />
            </div>

            <div className="form-group">
              <label>
                <IoMailOutline className="form-icon" />
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleInputChange}
                disabled={!isEditing || isSubmitting}
                required
              />
            </div>

            {isEditing && (
              <>
                <div className="form-group">
                  <label>
                    🔑 New Password (optional)
                  </label>
                  <div className="password-input-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={form.password}
                      onChange={handleInputChange}
                      placeholder="Leave blank to keep current password"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="password-toggle-btn"
                      disabled={isSubmitting}
                    >
                      {showPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    🔑 Confirm New Password
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your new password"
                    disabled={isSubmitting}
                  />
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="form-actions">
              {!isEditing ? (
                <button type="button" onClick={handleEdit} className="edit-btn">
                  <IoPersonOutline className="btn-icon" />
                  Edit Profile
                </button>
              ) : (
                <>
                  <button type="button" onClick={handleCancel} className="cancel-btn" disabled={isSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="save-btn" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div className="spinner-small"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <IoSaveOutline className="btn-icon" />
                        Save Changes
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </form>

          {/* Error/Success Messages */}
          {error && (
            <div className="error-message">
              <IoAlertCircleOutline className="error-icon" />
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              <IoCheckmarkCircleOutline className="success-icon" />
              {success}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile; 